<script setup lang="ts">
import type { BrokerSymbol, CategoryTranslationMap, SymbolCategory } from '~/api/wikitrade-market'
import { SEARCH_ICON_URL } from '~/pages/market/constants'
import { formatSymbolName, getCategoryName, getSymbolCategoryCode } from '~/pages/market/utils'

const { t } = useAppI18n()

const props = defineProps<{
  modelValue: boolean
  symbols: BrokerSymbol[]
  categories: SymbolCategory[]
  categoryTranslations: CategoryTranslationMap
  currentSymbol: string
  loading: boolean
  errorMessage: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  select: [symbol: string]
}>()

const keyword = ref('')
const activeCategory = ref('')

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const normalizedKeyword = computed(() => keyword.value.trim().toLowerCase())

// 分类翻译和分类列表来自两个接口，先合成 Map，后续 tab 和搜索都复用同一套名称。
const categoryNames = computed(
  () =>
    new Map(props.categories.map((category) => [category.code, getCategoryName(category, props.categoryTranslations)])),
)

// tab 数量根据当前可交易品种实时计算，避免展示接口返回但没有可用 symbol 的空分类。
const categoryTabs = computed(() => {
  const counts = new Map<string, number>()

  props.symbols.forEach((item) => {
    const code = getSymbolCategoryCode(item) || 'uncategorized'
    counts.set(code, (counts.get(code) || 0) + 1)
  })

  return [
    ...props.categories
      .map((category) => ({
        key: category.code,
        label: getCategoryName(category, props.categoryTranslations),
        count: counts.get(category.code) || 0,
      }))
      .filter((item) => item.count > 0),
    ...(counts.get('uncategorized')
      ? [
          {
            key: 'uncategorized',
            label: t('82993'),
            count: counts.get('uncategorized') || 0,
          },
        ]
      : []),
  ]
})

// 打开弹窗时把筛选条件回到当前品种所在分类，用户能直接看到已选品种附近的候选项。
const resetFilter = () => {
  keyword.value = ''
  const selectedSymbol = props.symbols.find((item) => item.symbol === props.currentSymbol)
  const selectedCategory = selectedSymbol ? getSymbolCategoryCode(selectedSymbol) : ''
  activeCategory.value =
    categoryTabs.value.find((tab) => tab.key === selectedCategory)?.key || categoryTabs.value[0]?.key || ''
}

// 保证当前激活分类始终在有效分类列表中，若失效则默认选中第一个。
watch(
  categoryTabs,
  (tabs) => {
    if (tabs.length > 0 && (!activeCategory.value || !tabs.some((tab) => tab.key === activeCategory.value))) {
      activeCategory.value = tabs[0]?.key ?? ''
    }
  },
  { immediate: true },
)

// 搜索同时匹配原始 symbol、展示名称和分类名称，方便用户按任一口径定位品种。
const filteredSymbols = computed(() => {
  const query = normalizedKeyword.value

  return props.symbols.filter((item) => {
    const categoryCode = getSymbolCategoryCode(item) || 'uncategorized'

    if (categoryCode !== activeCategory.value) {
      return false
    }

    if (!query) return true

    return `${item.symbol} ${formatSymbolName(item)} ${categoryNames.value.get(categoryCode) || ''}`
      .toLowerCase()
      .includes(query)
  })
})

const selectSymbol = (nextSymbol: string) => {
  emit('select', nextSymbol)
}
</script>

<template>
  <ElDialog v-model="dialogVisible" width="820px" align-center class="wikitrade-symbol-dialog" :close-on-click-modal="false" @open="resetFilter">
    <div class="wikitrade-symbol-dialog__body">
      <ElInput v-model="keyword" clearable :placeholder="t('82927')" size="large">
        <template #prefix>
          <CommonImage :src="SEARCH_ICON_URL" class="wikitrade-search-icon" alt="search" />
        </template>
      </ElInput>

      <!-- 分类 tab 根据可用 symbol 动态计数，避免展示没有品种的分类。 -->
      <div class="wikitrade-symbol-tabs" role="tablist">
        <button
          v-for="tab in categoryTabs"
          :key="tab.key"
          type="button"
          class="wikitrade-symbol-tab"
          :class="{ 'is-active': tab.key === activeCategory }"
          role="tab"
          :aria-selected="tab.key === activeCategory"
          @click="activeCategory = tab.key"
        >
          <span class="wikitrade-symbol-tab__label">{{ tab.label }}</span>
          <span class="wikitrade-symbol-tab__count">{{ tab.count }}</span>
        </button>
      </div>

      <ElAlert v-if="errorMessage" :title="errorMessage" type="warning" show-icon :closable="false" />

      <!-- 品种列表只负责选择 symbol；切换后的历史数据和实时订阅由页面 datafeed watcher 接管。 -->
      <div v-loading="loading" class="wikitrade-symbol-list">
        <ElScrollbar v-if="filteredSymbols.length" height="430px">
          <button
            v-for="item in filteredSymbols"
            :key="item.symbol"
            type="button"
            class="wikitrade-symbol-row"
            :class="{ 'is-active': item.symbol === currentSymbol }"
            @click="selectSymbol(item.symbol)"
          >
            <span class="wikitrade-symbol-row__main">
              <ElImage
                v-if="item.icon"
                class="wikitrade-symbol-row__icon"
                :src="String(item.icon)"
                :alt="item.symbol"
                fit="contain"
              />
              <span class="wikitrade-symbol-row__name">{{ formatSymbolName(item) }}</span>
            </span>
            <ElTag v-if="getSymbolCategoryCode(item)" effect="plain" round>
              {{ categoryNames.get(getSymbolCategoryCode(item)) || getSymbolCategoryCode(item) }}
            </ElTag>
          </button>
        </ElScrollbar>

        <ElEmpty v-else :description="loading ? '' : t('80359')" />
      </div>
    </div>
  </ElDialog>
</template>

<style scoped lang="less">
.wikitrade-symbol-dialog {
  :deep(.el-dialog__body) {
    padding-top: 8px;
  }
}

.wikitrade-symbol-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.wikitrade-search-icon {
  width: 16px;
  height: 16px;
}

.wikitrade-symbol-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.wikitrade-symbol-tab {
  display: inline-flex;
  height: 34px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid #e0e5ef;
  border-radius: 6px;
  background: #ffffff;
  color: #53627c;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;

  &.is-active {
    border-color: #0f4fd6;
    background: #edf4ff;
    color: #0f4fd6;
    font-weight: 700;
  }
}

.wikitrade-symbol-tab__count {
  color: inherit;
  opacity: 0.72;
}

.wikitrade-symbol-list {
  min-height: 220px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.wikitrade-symbol-row {
  display: flex;
  width: 100%;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid #edf0f5;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.18s ease,
    color 0.18s ease;

  &:hover {
    background: #f6f9ff;
  }

  &.is-active {
    background: #edf4ff;
    color: #0f4fd6;
  }
}

.wikitrade-symbol-row__main {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 12px;
}

.wikitrade-symbol-row__icon {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border-radius: 4px;
}

.wikitrade-symbol-row__name {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
